import type { ReactNode } from "react";
import { Alert as MuiAlert, Box, CircularProgress, Divider, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountSources, { AttackingSourcesTable } from "./AccountSources.tsx";
import AccountTimeline from "./AccountTimeline.tsx";
import CopyHashButton from "./CopyHashButton.tsx";
import { DataTable } from "@/components/DesignSystem";
import { useAlertDrawer } from "@/hooks/useAlerts.ts";
import { ApiError } from "@/services/api.ts";
import {
  ACCOUNT_HISTORY_GONE,
  ALERT_GONE,
  alertSummary,
  channelLabel,
  LOAD_ALERT_FAILED,
  NOT_SENT,
  whatToCheck,
  whyItFired,
} from "@/utils/alertCopy.ts";
import { formatDateTime } from "@/utils/formatDateTime.ts";
import type { Account, AlertDetail, AlertDrawerData, AlertNotification } from "@/types";

const NOTIFIED_COLUMNS = [
  { header: "Channel", accessor: (n: AlertNotification) => channelLabel(n.channel) },
  { header: "Recipient", accessor: (n: AlertNotification) => n.recipient },
  { header: "Time", accessor: (n: AlertNotification) => formatDateTime(n.at) },
  {
    header: "Result",
    accessor: (n: AlertNotification) =>
      n.delivered ? (
        "Sent"
      ) : (
        <Box component="span" sx={{ color: "error.main" }}>{`Failed: ${n.error ?? "unknown error"}`}</Box>
      ),
  },
];

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function ThisAlert({ alert }: { readonly alert: AlertDetail }) {
  const check = whatToCheck(alert.rule);
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, pr: 2 }}>
        {alertSummary(alert)}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {whyItFired(alert)}
      </Typography>
      {check && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <strong>What to check:</strong> {check}
        </Typography>
      )}
    </Box>
  );
}

function Notified({ notifications }: { readonly notifications: readonly AlertNotification[] }) {
  if (notifications.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {NOT_SENT}
      </Typography>
    );
  }
  return (
    <DataTable
      columns={NOTIFIED_COLUMNS}
      rows={notifications}
      getRowKey={(n) => `${n.channel}:${n.recipient}:${n.at}:${n.delivered}`}
    />
  );
}

function TheAccount({ alert, account }: { readonly alert: AlertDetail; readonly account: Account | null }) {
  return (
    <Section title="The account">
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
        <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
          {alert.account_hash}
        </Typography>
        <CopyHashButton hash={alert.account_hash} />
      </Box>
      {account ? (
        <AccountSources sources={account.sources} />
      ) : (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {ACCOUNT_HISTORY_GONE}
          </Typography>
          <AttackingSourcesTable sources={alert.evidence.sources} />
        </>
      )}
    </Section>
  );
}

function DrawerContent({
  data,
  onOpenAlert,
}: {
  readonly data: AlertDrawerData;
  readonly onOpenAlert: (id: string) => void;
}) {
  const { alert, account } = data;
  return (
    <>
      <ThisAlert alert={alert} />
      <Section title="Notified">
        <Notified notifications={alert.notifications} />
      </Section>
      <TheAccount alert={alert} account={account} />
      {account && (
        <Section title="Timeline">
          <AccountTimeline
            events={account.events}
            alerts={account.alerts}
            currentAlertId={alert.id}
            onOpenAlert={onOpenAlert}
          />
        </Section>
      )}
    </>
  );
}

function DrawerBody({
  data,
  isLoading,
  error,
  onOpenAlert,
}: {
  readonly data: AlertDrawerData | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onOpenAlert: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error && !data) {
    return error instanceof ApiError && error.status === 404 ? (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {ALERT_GONE}
      </Typography>
    ) : (
      <MuiAlert severity="error">{LOAD_ALERT_FAILED}</MuiAlert>
    );
  }
  if (!data) return null;
  return <DrawerContent data={data} onOpenAlert={onOpenAlert} />;
}

/** One alert with who was notified, the account's sources and its event trail. */
export default function AlertDrawer({
  alertId,
  onClose,
  onOpenAlert,
}: {
  readonly alertId: string | null;
  readonly onClose: () => void;
  readonly onOpenAlert: (id: string) => void;
}) {
  const { data, isLoading, error } = useAlertDrawer(alertId);
  return (
    <Drawer
      anchor="right"
      open={alertId !== null}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 600 } } } }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DrawerBody data={data} isLoading={isLoading} error={error} onOpenAlert={onOpenAlert} />
      </Box>
    </Drawer>
  );
}
